import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import logoCompass from '@/assets/logo-compass.png';
import logoWordmark from '@/assets/logo-wordmark.png';
import { LibraryTab } from '@/components/tabs/LibraryTab';
import { CardsTab } from '@/components/tabs/CardsTab';
import { CommunityTab } from '@/components/tabs/CommunityTab';
import { LearnTab } from '@/components/tabs/LearnTab';
import { ProfileTab } from '@/components/tabs/ProfileTab';
import { NewsTab } from '@/components/tabs/NewsTab';
import { AuthScreen } from '@/components/AuthScreen';
import { AuthPrompt } from '@/components/AuthPrompt';
import { useAuth } from '@/hooks/useAuth';
import { useRealtimeSubscriptions } from '@/hooks/useRealtime';
import { BookOpen, Layers, MessageCircle, Library, User, LogIn, Newspaper, ChevronLeft, ChevronRight } from 'lucide-react';

const TABS = [
  { id: 'news', label: 'News', icon: Newspaper },
  { id: 'library', label: 'Library', icon: Library },
  { id: 'collection', label: 'Collection', icon: Layers, requiresAuth: true },
  { id: 'community', label: 'Community', icon: MessageCircle },
  { id: 'learn', label: 'Learn', icon: BookOpen },
  { id: 'profile', label: 'Profile', icon: User, requiresAuth: true },
] as const;

const Index = () => {
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('news');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useRealtimeSubscriptions();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center flex flex-col items-center">
          <img src={logoCompass} alt="LoreScout app loading" width={56} height={56} fetchPriority="high" className="h-14 w-14 animate-spin" style={{ animationDuration: '3s' }} />
        </div>
      </div>
    );
  }

  const handleTabClick = (tabId: string) => {
    const tab = TABS.find(t => t.id === tabId);
    if (tab && 'requiresAuth' in tab && tab.requiresAuth && !user) {
      setShowAuthModal(true);
      return;
    }
    setActiveTab(tabId);
  };

  const renderTabContent = () => {
    if (activeTab === 'collection' && !user) {
      return <AuthPrompt message="Sign in to manage your collection" />;
    }
    if (activeTab === 'profile' && !user) {
      return <AuthPrompt message="Sign in to view your profile" />;
    }

    switch (activeTab) {
      case 'news': return <NewsTab />;
      case 'library': return <LibraryTab />;
      case 'collection': return <CardsTab />;
      case 'community': return <CommunityTab onRequestAuth={() => setShowAuthModal(true)} />;
      case 'learn': return <LearnTab />;
      case 'profile': return <ProfileTab />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <Helmet>
        <link rel="canonical" href="https://lorescout.com/" />
        <meta property="og:url" content="https://lorescout.com/" />
      </Helmet>

      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex md:flex-col md:fixed md:inset-y-0 md:left-0 z-40 border-r border-border bg-card/95 backdrop-blur-lg transition-[width] duration-200 ${
          sidebarCollapsed ? 'md:w-16' : 'md:w-60'
        }`}
      >
        <div className={`flex items-center gap-2 px-4 pt-5 pb-4 ${sidebarCollapsed ? 'justify-center px-2' : ''}`}>
          <img src={logoCompass} alt="" aria-hidden="true" width={32} height={32} className="h-8 w-8 flex-shrink-0" />
          {!sidebarCollapsed && (
            <img src={logoWordmark} alt="LoreScout logo" width={160} height={40} fetchPriority="high" className="w-36 object-contain" />
          )}
        </div>
        <nav className="flex-1 px-2 space-y-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                title={tab.label}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium font-body transition-colors ${
                  isActive
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                } ${sidebarCollapsed ? 'justify-center' : ''}`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>{tab.label}</span>}
              </button>
            );
          })}
        </nav>
        <div className="p-2 border-t border-border">
          {!user && (
            <button
              onClick={() => setShowAuthModal(true)}
              className={`w-full flex items-center gap-2 rounded-lg bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground font-body hover:bg-primary/90 transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}
            >
              <LogIn className="h-4 w-4" />
              {!sidebarCollapsed && <span>Sign In</span>}
            </button>
          )}
          <button
            onClick={() => setSidebarCollapsed(v => !v)}
            className="mt-2 w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground font-body transition-colors"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <><ChevronLeft className="h-4 w-4" /><span>Collapse</span></>}
          </button>
        </div>
      </aside>

      {/* Mobile header with tabs */}
      <header className="md:hidden sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-lg app-container w-full">
        <h1 className="sr-only">LoreScout — Disney Lorcana TCG Companion</h1>
        <div className="px-4 pt-3 pb-0">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src={logoCompass} alt="" aria-hidden="true" width={32} height={32} className="h-8 w-8" />
            <img src={logoWordmark} alt="LoreScout logo" width={192} height={48} fetchPriority="high" className="w-48 object-contain" />
            <img src={logoCompass} alt="" aria-hidden="true" width={32} height={32} className="h-8 w-8" />
          </div>
          <nav className="flex">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`flex flex-1 flex-col items-center gap-0.5 pb-2.5 pt-1 transition-colors relative ${
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-[10px] font-medium font-body">{tab.label}</span>
                  {isActive && (
                    <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 rounded-full bg-primary" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      {/* Main content */}
      <main
        className={`flex-1 overflow-y-auto transition-[margin] duration-200 ${
          sidebarCollapsed ? 'md:ml-16' : 'md:ml-60'
        }`}
      >
        <div className="app-container md:max-w-5xl md:mx-auto md:px-6 md:py-6">
          {renderTabContent()}
        </div>
      </main>

      {/* Floating Sign In button (mobile only) */}
      {!user && !showAuthModal && (
        <button
          onClick={() => setShowAuthModal(true)}
          className="md:hidden fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground font-body shadow-lg shadow-primary/30 hover:bg-primary/90 transition-colors"
        >
          <LogIn className="h-4 w-4" /> Sign In
        </button>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/80 backdrop-blur-sm" onClick={() => setShowAuthModal(false)}>
          <div className="w-full max-w-sm mx-4 rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <AuthScreen
              onSuccess={() => setShowAuthModal(false)}
              compact
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
